// import { FreeOnboarding } from "@/pages/Onboarding/FreeOnboarding";
import { withMixpanel } from "@/events/mixpanelClient";
import { mixpanelTrack } from "@/events/mixpanelTrack";
import { loadChurnkey } from "@/lib/churnkey";
import { CHURNKEY_APP_ID } from "@/settings";
import { useShowAds } from "@repo/shared/redux";
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router";

import { usePageVisibility } from "../../../hooks/usePageVisibility";
import { selectClubImportState } from "../../../redux/clubImport/clubImportSlice";
import { globalStateSelector } from "../../../redux/globalState/globalStateSlice";
import {
  getMessageSelector,
  showDialog,
} from "../../../redux/message/messageSlice";
import { AppDispatch } from "../../../redux/store";
import {
  fetchUserNotifications,
  getUserSelector,
  markNotificationAsRead,
} from "../../../redux/user/userSlice";
import { Notifications } from "../Notifications/Notifications";

type Props = {
  children: React.ReactNode;
  isLoggedIn: boolean;
};

const AppWrapper = ({ children, isLoggedIn }: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { userNotifications } = useSelector(getUserSelector);
  const { isCurrentlyImporting, isCurrentlyOnboarding } =
    useSelector(globalStateSelector);
  const { alerts } = useSelector(getMessageSelector);
  const { user } = useSelector(getUserSelector);
  const { browser, extensionEnabledStatus } = useSelector(
    selectClubImportState,
  );
  const showAds = useShowAds();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const disabledAds =
      !showAds ||
      [
        "/get-my-players",
        "/get-sbc-solver",
        "/get-ai-squad-builder",
        "/get-ultimate",
        "/subscription",
        "/creator",
        "/profile",
        "/meta-rating",
        "/my-club",
      ].includes(location.pathname);

    if (disabledAds || isCurrentlyOnboarding) {
      document.body.classList.add("paying-user");
    } else {
      document.body.classList.remove("paying-user");
    }
    return () => {
      document.body.classList.remove("paying-user");
    };
  }, [location.pathname, showAds, isCurrentlyOnboarding]);

  const handleVisibilityChange = useCallback(
    (visible: boolean) => {
      const blockedPages = ["import"];
      if (
        isLoggedIn &&
        visible &&
        !blockedPages.includes(window.location.pathname) &&
        !isCurrentlyImporting
      ) {
        dispatch(fetchUserNotifications());
      }
    },
    [isLoggedIn, dispatch, isCurrentlyImporting],
  );

  usePageVisibility(handleVisibilityChange);

  useEffect(() => {
    const askReview = userNotifications.find((n) => n?.type === "ASK_REVIEW");
    if (
      !askReview ||
      browser !== "desktopChrome" ||
      extensionEnabledStatus !== "enabled"
    )
      return;

    dispatch(markNotificationAsRead({ notificationId: askReview.id }));
    dispatch(showDialog({ modalType: "extension-review" }));
  }, [userNotifications, browser, extensionEnabledStatus, dispatch]);

  useEffect(() => {
    const viaParam = searchParams.get("via");
    if (!viaParam) return;

    mixpanelTrack("channel_partnerships", { via: viaParam });

    searchParams.delete("via");
    setSearchParams(searchParams, { replace: true });

    if (viaParam === "boras") {
      navigate(`/creator?v=boras`);
    } else if (viaParam === "aj3") {
      navigate(`/creator?v=aj3`);
    }
  }, []);

  useEffect(() => {
    const { customerId, churnkeyHash } = user;
    if (!customerId || !churnkeyHash) return;

    let cancelled = false;
    loadChurnkey()
      .then(() => {
        if (cancelled || !window.churnkey?.check) return;
        window.churnkey.check("pause", {
          customerId,
          authHash: churnkeyHash,
          appId: CHURNKEY_APP_ID,
          provider: "stripe",
          onPauseWallActivated: () => {
            mixpanelTrack("churnkey_pause_wall_activated");
            withMixpanel((mp) =>
              mp.people.set({ $churnkey_pause_wall_activated: true }),
            );
          },
          onResume: (customer: string) => {
            mixpanelTrack("churnkey_resume", { customer });
          },
          onCancel: (customer: string) => {
            mixpanelTrack("churnkey_cancel", { customer });
          },
        });
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [user.customerId, user.churnkeyHash]);

  return (
    <div className="min-h-screen">
      {/* <FreeOnboarding isCurrentlyOnboarding={isCurrentlyOnboarding}>
      </FreeOnboarding> */}
      {children}
      {(userNotifications.length > 0 || alerts.length > 0) && (
        <Notifications userNotifications={userNotifications} alerts={alerts} />
      )}
    </div>
  );
};

export { AppWrapper };
